import React from 'react';
import { Chip, Tooltip } from '@mui/material';

// One shared palette + chip so every clinical list (medications, diagnoses,
// allergies, encounters) speaks the same visual language.
export type BadgeColor = 'error' | 'warning' | 'info' | 'success' | 'default';

// Allergy / reaction severity → colour.
export const severityColor = (s: string): BadgeColor => {
  const t = (s || '').toLowerCase();
  if (/anaphyl|life|severe|epipen/.test(t)) return 'error';
  if (/moder/.test(t)) return 'warning';
  if (/mild/.test(t)) return 'info';
  return 'default';
};

// Diagnosis status → colour (active is the implicit default and usually shown
// without a chip; chronic/resolved are flagged).
export const statusColor = (status?: string | null): BadgeColor => {
  const t = (status || '').toLowerCase();
  if (t === 'resolved') return 'default';
  if (t === 'chronic') return 'info';
  if (t === 'active') return 'success';
  return 'default';
};

// Encounter type → colour (telehealth stands out from in-person).
export const encounterColor = (isVirtual?: boolean): BadgeColor => (isVirtual ? 'info' : 'default');

// Small, consistent clinical chip. `variant="outlined"` is used for provenance
// (e.g. the "PF" source tag); filled is used for status/severity.
export const ClinicalChip: React.FC<{
  label: string;
  color?: BadgeColor;
  variant?: 'filled' | 'outlined';
  tooltip?: string;
}> = ({ label, color = 'default', variant = 'filled', tooltip }) => {
  const chip = (
    <Chip
      label={label}
      size="small"
      color={color === 'default' ? undefined : color}
      variant={variant}
      sx={{
        height: 16,
        borderRadius: 0.5,
        '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem', fontWeight: 700 },
      }}
    />
  );
  return tooltip ? (
    <Tooltip title={tooltip} placement="top-start">
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

// The provenance tag ("PF", "Spruce", …) used across the clinical lists.
export const SourceChip: React.FC<{ source?: string | null }> = ({ source }) => {
  if (!source || source === 'manual') return null;
  const label = source === 'pf_import' ? 'PF' : source === 'spruce' ? 'Spruce' : source;
  return <ClinicalChip label={label} color="info" variant="outlined" tooltip={`Source: ${source}`} />;
};
